import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';
import ConfirmationModal from './ConfirmationModal';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  LinearProgress,
  Paper,
  Chip,
  Divider,
  useMediaQuery,
} from '@mui/material';
import {
  Quiz as QuizIcon,
  Save as SaveIcon,
  Send as SubmitIcon,
  OpenInNew as OpenDocIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';

interface Question {
  id: number;
  question_type: string;
  question_text: string;
  options: { label: string; text: string }[] | null;
  points_possible: number;
  order_index: number;
}

const StudentQuizTaking: React.FC = () => {
  const { quizAssignmentId } = useParams<{ quizAssignmentId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quizTitle, setQuizTitle] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Record<number, { student_answer?: string; selected_option?: string }>>({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState(false);
  const [assignmentId, setAssignmentId] = useState<string | null>(null);

  useEffect(() => {
    if (quizAssignmentId) loadQuizAndStart();
  }, [quizAssignmentId]);

  const loadQuizAndStart = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      // We need to find the assignment_id from the URL. The quizAssignmentId IS the qa id.
      // First, get quiz info. We'll get the assignment_id from the query params or just use the start endpoint.
      // Start or resume the attempt
      const startResp = await axios.post(
        `${API_BASE_URL}/api/student/quiz-attempts/${quizAssignmentId}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAttemptId(startResp.data.attempt_id);
      const savedResponses = startResp.data.saved_responses || {};

      // Now fetch quiz questions - we need assignment_id. Let's get it from the QA via assignments endpoint.
      // Actually, let's pass assignment_id via query param from the StudentAssignments page.
      const params = new URLSearchParams(window.location.search);
      const aId = params.get('assignmentId');
      setAssignmentId(aId);

      if (aId) {
        const quizResp = await axios.get(
          `${API_BASE_URL}/api/student/assignments/${aId}/quiz`,
          { headers: { Authorization: `Bearer ${token}` }, params: { quiz_assignment_id: quizAssignmentId } }
        );

        setQuizTitle(quizResp.data.quiz_title || '');
        setQuestions(quizResp.data.questions || []);

        // Restore saved answers
        const restored: Record<number, { student_answer?: string; selected_option?: string }> = {};
        for (const q of quizResp.data.questions || []) {
          const saved = savedResponses[q.id];
          if (saved) {
            restored[q.id] = saved;
          }
        }
        setAnswers(restored);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: number, field: 'student_answer' | 'selected_option', value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], [field]: value },
    }));
  };

  const buildResponsesPayload = () => {
    return Object.entries(answers).map(([qId, ans]) => ({
      question_id: parseInt(qId),
      student_answer: ans.student_answer || null,
      selected_option: ans.selected_option || null,
    }));
  };

  const handleSave = async () => {
    if (!attemptId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API_BASE_URL}/api/student/quiz-attempts/${attemptId}/save`,
        { responses: buildResponsesPayload() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!attemptId) return;
    setSubmitConfirm(false);
    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await axios.post(
        `${API_BASE_URL}/api/student/quiz-attempts/${attemptId}/submit`,
        { responses: buildResponsesPayload() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data.success) {
        navigate(`/student/quiz/${quizAssignmentId}/results/${attemptId}?assignmentId=${assignmentId}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDocument = async () => {
    if (!assignmentId) return;
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await axios.get(
        `${API_BASE_URL}/api/student/assignments/${assignmentId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data.download_url) {
        window.open(resp.data.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to open document:', err);
    }
  };

  const answeredCount = questions.filter((q) => {
    const ans = answers[q.id];
    if (!ans) return false;
    if (q.question_type === 'multiple_choice') return !!ans.selected_option;
    return !!ans.student_answer?.trim();
  }).length;

  const totalPoints = questions.reduce((sum, q) => sum + (q.points_possible || 1), 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: isMobile ? 2 : 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/student/assignments')} size="small">
          {t('common.back')}
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <QuizIcon sx={{ color: '#1565c0' }} />
          <Typography variant="h5" fontWeight={700}>{quizTitle}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`${questions.length} ${t('quiz.taking.questions')}`} size="small" variant="outlined" />
          <Chip label={`${totalPoints} ${t('quiz.taking.totalPoints')}`} size="small" variant="outlined" />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('quiz.taking.progress')}: {answeredCount} / {questions.length}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenDocIcon />}
            onClick={handleViewDocument}
            aria-label={t('quiz.taking.viewDocument')}
          >
            {t('quiz.taking.viewDocument')}
          </Button>
        </Box>
        <LinearProgress
          variant="determinate"
          value={questions.length > 0 ? (answeredCount / questions.length) * 100 : 0}
          sx={{ borderRadius: 1, height: 6 }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Questions */}
      {questions.map((q, idx) => (
        <Paper key={q.id} sx={{ p: 3, mb: 2, borderLeft: '4px solid #1565c0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('quiz.taking.questionNum', { num: idx + 1, total: questions.length })}
            </Typography>
            <Chip
              label={`${q.points_possible} ${q.points_possible === 1 ? t('quiz.taking.point') : t('quiz.taking.points')}`}
              size="small"
              variant="outlined"
            />
          </Box>

          <Typography variant="body1" fontWeight={500} sx={{ mb: 2 }}>
            {q.question_text}
          </Typography>

          {q.question_type === 'multiple_choice' && q.options && (
            <RadioGroup
              value={answers[q.id]?.selected_option || ''}
              onChange={(e) => handleAnswerChange(q.id, 'selected_option', e.target.value)}
            >
              {q.options.map((opt) => (
                <FormControlLabel
                  key={opt.label}
                  value={opt.label}
                  control={<Radio />}
                  label={`${opt.label}) ${opt.text}`}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </RadioGroup>
          )}

          {q.question_type === 'short_answer' && (
            <TextField
              multiline
              rows={3}
              fullWidth
              placeholder={t('quiz.taking.shortAnswerPlaceholder')}
              value={answers[q.id]?.student_answer || ''}
              onChange={(e) => handleAnswerChange(q.id, 'student_answer', e.target.value)}
              aria-label={t('quiz.taking.yourAnswer')}
            />
          )}

          {(q.question_type === 'cloze' || q.question_type === 'vocabulary') && (
            <TextField
              fullWidth
              placeholder={
                q.question_type === 'cloze'
                  ? t('quiz.taking.clozePlaceholder')
                  : t('quiz.taking.vocabPlaceholder')
              }
              value={answers[q.id]?.student_answer || ''}
              onChange={(e) => handleAnswerChange(q.id, 'student_answer', e.target.value)}
              aria-label={t('quiz.taking.yourAnswer')}
            />
          )}
        </Paper>
      ))}

      {/* Action bar */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'stretch', sm: 'flex-end' }, flexDirection: { xs: 'column', sm: 'row' }, mt: 3, mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || submitting}
          aria-label={t('quiz.taking.saveDraft')}
        >
          {t('quiz.taking.saveDraft')}
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SubmitIcon />}
          onClick={() => setSubmitConfirm(true)}
          disabled={saving || submitting}
          sx={{ bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
          aria-label={t('quiz.taking.submitQuiz')}
        >
          {t('quiz.taking.submitQuiz')}
        </Button>
      </Box>

      <ConfirmationModal
        isOpen={submitConfirm}
        title={t('quiz.taking.confirmSubmitTitle')}
        message={t('quiz.taking.confirmSubmitMessage', { answered: answeredCount, total: questions.length })}
        variant="warning"
        confirmText={t('quiz.taking.submitQuiz')}
        onConfirm={handleSubmit}
        onCancel={() => setSubmitConfirm(false)}
      />
    </Box>
  );
};

export default StudentQuizTaking;
